import { createSlice } from '@reduxjs/toolkit';

export const CHAT_PAGE_SIZE = 50;

function withDefaultStatus(message) {
    return message?.delivery_status ? message : { ...message, delivery_status: 'delivered' };
}

function applyCachedFailure(state, message) {
    const cached = state.pendingFailures?.[message.chat_message_id];
    if (!cached) return message;
    // eslint-disable-next-line no-param-reassign
    delete state.pendingFailures[message.chat_message_id];
    return { ...message, delivery_status: 'failed', failure_reason: cached.reason };
}

function clearQueuedOnRecovery(state, conversationId) {
    state.messages = state.messages.map((m) => (
        m.conversation_id === conversationId && m.delivery_status === 'queued'
            ? { ...m, delivery_status: 'delivered' }
            : m
    ));
}

function bumpConversationPreview(state, message) {
    if (!message?.conversation_id) return;
    const idx = state.conversations.findIndex(
        (c) => c.chat_conversation_id === message.conversation_id
    );
    if (idx === -1) return;
    const when = message.created_date || new Date().toISOString();
    const existing = state.conversations[idx];
    const existingWhen = existing.updated_date || existing.created_date;
    if (existingWhen && new Date(existingWhen) > new Date(when)) return;
    const nextBody = message.body || existing.last_message_body;
    if (existing.updated_date === when && existing.last_message_body === nextBody) return;
    state.conversations[idx] = {
        ...existing,
        last_message_body: nextBody,
        updated_date: when
    };
}

function appendMessageIfNew(state, message) {
    const exists = state.messages.some(m => m.chat_message_id === message.chat_message_id);
    if (!exists) {
        const prepared = applyCachedFailure(state, withDefaultStatus(message));
        // TODO: cap at ~500 messages. Long sessions grow unboundedly; full list re-renders on each append.
        state.messages = [...state.messages, prepared];
        return true;
    }
    return false;
}

export const liveChatSlice = createSlice({
    name: 'liveChat',
    initialState: {
        conversations: [],
        isConversationsFetched: false,
        activeConversation: null,
        messages: [],
        isMessagesFetched: false,
        isLoadingMore: false,
        hasMoreMessages: true,
        isSending: false,
        isCreating: false,
        drawerOpen: false,
        agentTyping: false,
        unreadCount: 0,
        pendingMessage: null,
        pendingFailures: {}
    },
    reducers: {
        onCallConversations: (state) => {
            state.isConversationsFetched = false;
        },
        onSuccessConversations: (state, { payload }) => {
            state.isConversationsFetched = true;
            state.conversations = payload;
        },
        onFailedConversations: (state) => {
            state.isConversationsFetched = true;
            state.conversations = [];
        },

        onCallCreateConversation: (state) => {
            state.isCreating = true;
        },
        onSuccessCreateConversation: (state, { payload }) => {
            state.isCreating = false;
            state.activeConversation = payload;
            state.conversations = [payload, ...state.conversations];
            state.messages = [];
            state.isMessagesFetched = true;
            state.hasMoreMessages = false;
        },
        onFailedCreateConversation: (state) => {
            state.isCreating = false;
        },

        onCallMessages: (state, { payload }) => {
            if (payload?.offset === 0) {
                state.isMessagesFetched = false;
                state.messages = [];
            } else {
                state.isLoadingMore = true;
            }
        },
        onSuccessMessages: (state, { payload }) => {
            const { messages, offset } = payload;
            state.isMessagesFetched = true;
            state.isLoadingMore = false;
            const withStatus = messages.map((m) => applyCachedFailure(state, withDefaultStatus(m)));
            if (offset === 0) {
                state.messages = withStatus;
            } else {
                state.messages = [...withStatus, ...state.messages];
            }
            state.hasMoreMessages = messages.length >= CHAT_PAGE_SIZE;
        },
        onFailedMessages: (state) => {
            state.isMessagesFetched = true;
            state.isLoadingMore = false;
        },

        onCallSendMessage: (state) => {
            state.isSending = true;
        },
        onSuccessSendMessage: (state, { payload }) => {
            state.isSending = false;
            appendMessageIfNew(state, payload);
            bumpConversationPreview(state, payload);
        },

        markMessageFailed: (state, { payload }) => {
            const { conversation_id, message_id, reason } = payload;
            const idx = state.messages.findIndex((m) => m.chat_message_id === message_id);
            if (idx === -1) {
                state.pendingFailures[message_id] = { reason, conversation_id };
                return;
            }
            state.messages[idx] = {
                ...state.messages[idx],
                delivery_status: 'failed',
                failure_reason: reason
            };
        },

        markMessageQueued: (state, { payload }) => {
            const { conversation_id } = payload;
            for (let i = state.messages.length - 1; i >= 0; i--) {
                const m = state.messages[i];
                const skippable =
                    m.conversation_id !== conversation_id
                    || m.sender_type !== 'visitor'
                    || m.delivery_status === 'failed'
                    || m.delivery_status === 'retrying';
                if (!skippable) {
                    if (m.delivery_status === 'queued') return;
                    state.messages[i] = { ...m, delivery_status: 'queued' };
                    return;
                }
            }
        },

        setMessageDeliveryStatus: (state, { payload }) => {
            const { message_id, delivery_status } = payload;
            const idx = state.messages.findIndex((m) => m.chat_message_id === message_id);
            if (idx !== -1) {
                state.messages[idx] = { ...state.messages[idx], delivery_status };
            }
        },
        onFailedSendMessage: (state) => {
            state.isSending = false;
        },

        addIncomingMessage: (state, { payload }) => {
            bumpConversationPreview(state, payload);
            const activeId = state.activeConversation?.chat_conversation_id;
            if (activeId && payload.conversation_id !== activeId) {
                if (!state.drawerOpen) {
                    state.unreadCount += 1;
                }
                return;
            }
            // Pipeline recovered — clear 'queued' on other visitor messages in
            // this conversation. True in-flight 'pending' messages clear via
            // their own NEW_MESSAGE dedup path.
            clearQueuedOnRecovery(state, payload.conversation_id);
            if (appendMessageIfNew(state, payload) && !state.drawerOpen) {
                state.unreadCount += 1;
            }
        },

        onSuccessUpdateStatus: (state, { payload }) => {
            const { conversationId, status, updated_date } = payload;
            const when = updated_date || new Date().toISOString();
            state.conversations = state.conversations.map(c =>
                c.chat_conversation_id === conversationId
                    ? { ...c, status, updated_date: when }
                    : c
            );
            if (state.activeConversation?.chat_conversation_id === conversationId) {
                state.activeConversation = { ...state.activeConversation, status, updated_date: when };
            }
        },

        setActiveConversation: (state, { payload }) => {
            const sameId = payload
                && state.activeConversation?.chat_conversation_id === payload.chat_conversation_id;
            if (sameId) {
                state.activeConversation = payload;
                return;
            }
            state.activeConversation = payload;
            state.messages = [];
            state.isMessagesFetched = false;
            state.hasMoreMessages = true;
        },
        toggleChatDrawer: (state, { payload }) => {
            state.drawerOpen = payload !== undefined ? payload : !state.drawerOpen;
            if (state.drawerOpen) {
                state.unreadCount = 0;
            }
        },
        setAgentTyping: (state, { payload }) => {
            state.agentTyping = payload;
        },
        clearUnreadCount: (state) => {
            state.unreadCount = 0;
        },
        setPendingMessage: (state, { payload }) => {
            state.pendingMessage = payload;
        },

        patchMessageAttachments: (state, { payload }) => {
            const { conversation_id, message_id, attachments } = payload;
            if (state.activeConversation?.chat_conversation_id !== conversation_id) return;
            const idx = state.messages.findIndex((m) => m.chat_message_id === message_id);
            if (idx === -1) return;
            state.messages[idx] = { ...state.messages[idx], attachment: attachments };
        }
    }
});

export const {
    onCallConversations,
    onSuccessConversations,
    onFailedConversations,
    onCallCreateConversation,
    onSuccessCreateConversation,
    onFailedCreateConversation,
    onCallMessages,
    onSuccessMessages,
    onFailedMessages,
    onCallSendMessage,
    onSuccessSendMessage,
    onFailedSendMessage,
    addIncomingMessage,
    onSuccessUpdateStatus,
    setActiveConversation,
    toggleChatDrawer,
    setAgentTyping,
    clearUnreadCount,
    setPendingMessage,
    markMessageFailed,
    markMessageQueued,
    setMessageDeliveryStatus,
    patchMessageAttachments
} = liveChatSlice.actions;

export default liveChatSlice.reducer;
